import { ref, watchEffect } from 'vue';
import { defineStore } from 'pinia';
import { api } from '@/api';
import type { CurrentUserProfileDto, UserDto } from '@/api/generator';
import { Settings } from 'luxon';
import i18next from 'i18next';
import { isString } from 'lodash';
import { useBrowserStorage } from '@/utils/useBrowserStorage';
import { useAuthenticationStore } from './authenticationStore';

const enLanguage = 'en';

export const useAccountStore = defineStore('account', () => {
  const profile = ref<CurrentUserProfileDto>();

  const currentUser = ref<UserDto>();

  // new users will have default "en" in their pofile-default
  // so we set this as a default here
  const language = useBrowserStorage({
    key: 'language',
    defaultValue: enLanguage,
    validator: isString,
    type: 'localStorage',
  });
  const authenticationStore = useAuthenticationStore();

  // Installation-Routine should always be in english
  watchEffect(() => {
    if (authenticationStore.session?.isFirstTimeInstallation) {
      i18next.changeLanguage(enLanguage);
      language.value = enLanguage;
    }
  });

  // new users will have timezone of the server OS in their profile-default
  // so we set the browser timezone to be as close as possible (will not used anyways before signin)
  const timezone = useBrowserStorage({
    key: 'timezone',
    defaultValue: Intl.DateTimeFormat().resolvedOptions().timeZone,
    validator: isString,
    type: 'localStorage',
  });

  watchEffect(() => {
    Settings.defaultLocale = language.value;
  });

  watchEffect(() => {
    Settings.defaultZone = timezone.value;
  });

  async function setProfile(newProfile: CurrentUserProfileDto, isSignIn?: boolean) {
    // set profile at first place, to make sure ChangeLanguageForm is refreshed correctly
    profile.value = newProfile;
    timezone.value = newProfile.timezone;

    if (!isSignIn) {
      await setLanguage(newProfile);
    }
  }

  async function setLanguage(profile: CurrentUserProfileDto) {
    if (profile.language !== language.value) {
      if (i18next.isInitialized) {
        await i18next.changeLanguage(profile.language);
      }
      language.value = profile.language;
    }
  }

  async function getProfile(isSignIn?: boolean) {
    const { result } = await api.account.getCurrentUserProfile();
    if (result) {
      await setProfile(result, isSignIn);
    }
  }

  async function fetchAccountData(userId: number, isSignIn?: boolean) {
    await getProfile(isSignIn);
    const { result: userResult } = await api.usersApi.getUser({ id: userId });

    if (userResult) {
      currentUser.value = userResult;
    }
  }

  return {
    fetchAccountData,
    setProfile,
    profile,
    currentUser,
    language,
    timezone,
  };
});
